import { useSelector } from "react-redux";
import { selectChildPractices, selectCurrentCustomerNumber } from "../../../redux/customer";
import { useAppDispatch } from "../../../redux/store";
import { useEffect, useState } from "react";
import { DevicesComplianceCounts } from "../../../types";
import PageHeader from "../../../components/PageHeader";
import { deviceIcon } from "../../../icons"
import ComplianceReport from "./ComplianceReport";
import ExpiredItems from "./ExpiredItems";
import ExpiringItems from "./ExpiringItems";
import BackorderedItems from "./BackorderedItems";
import { Box, Button, Menu, MenuItem, Tab, Tabs } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { addError } from '../../../redux/alerts';
import { exportByType, exportKitsByPractice, exportResultsByPractice } from "./Reports";

const initComplianceCounts: DevicesComplianceCounts = {
  actionRequired: 0,
  attentionNeeded: 0,
  upToDate: 0,
  inactive: 0,
  activeCount: 0,
  backorderedCount: 0,
  expiringCount: 0,
  expiredCount: 0,
  upToDateAeds: 0,
  upToDateDeviceKits: 0,
  actionRequiredPercentage: 0,
  attentionNeededPercentage: 0,
  upToDatePercentage: 0,
  inactivePercentage: 0
};

const Devices = () => {
  const customerNumber = useSelector(selectCurrentCustomerNumber);
  const dispatch = useAppDispatch();
  const childPractices = useSelector(selectChildPractices);

  const [tabIndex, setTabIndex] = useState<number>(0);
  const [complianceCounts, setComplianceCounts] = useState(initComplianceCounts);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [exportOpenEl, setExportOpenEl] = useState<HTMLElement | null>(null);

  const exportOpen = Boolean(exportOpenEl);
  const pageTabs = ['Compliance Report', `Expired Items (${complianceCounts.expiredCount ?? 0})`, `Expiring Items (${complianceCounts.expiringCount ?? 0})`, `Backordered Items (${complianceCounts.backorderedCount ?? 0})`];

  const calculatePercentage = (count: number, total: number) => {
    const percentage = (count / total) * 100;

    if (isNaN(percentage)) {
      return 0;
    }
    return Math.round(percentage);
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportOpenEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportOpenEl(null);
  };

  const handleExportByType = async (type: string) => {
    handleExportClose();
    setExportLoading(true);

    try {
      if (type === 'resultsByPractice') {
        exportResultsByPractice(childPractices);
      } else if (type === 'kitsByPractice') {
        await exportKitsByPractice(customerNumber, childPractices);
      } else {
        await exportByType(customerNumber, type);
      }
      setExportLoading(false);
    } catch (error: any) {
      setExportLoading(false);
      const message = error?.response?.data?.message || error?.message || 'Failed to load report';
      dispatch(addError(message));
    }
  }

  useEffect(() => {
    if (childPractices.length > 0) {
      const complianceCounts = structuredClone(initComplianceCounts);

      childPractices.forEach(practice => {
        switch (practice.device_status) {
          case 1:
            complianceCounts.upToDate++;
            break;
          case 2:
            complianceCounts.attentionNeeded++;
            break;
          case 3:
            complianceCounts.actionRequired++;
            break;
          case 4:
            complianceCounts.inactive++;
            break;
          default:
            complianceCounts.inactive++;
        }

        complianceCounts.activeCount += practice.active_device_kits;
        complianceCounts.backorderedCount += practice.backordered_devices;
        complianceCounts.expiredCount += practice.expired_devices;
        complianceCounts.expiredCount += practice.expired_refilling_devices;
        complianceCounts.expiringCount += practice.expiring_devices;
        complianceCounts.upToDateAeds += practice.compliant_aed_devices;
        complianceCounts.upToDateDeviceKits += practice.active_device_kits;
      })

      const totalPractices = childPractices.length;
      complianceCounts.actionRequiredPercentage = calculatePercentage(complianceCounts.actionRequired, totalPractices);
      complianceCounts.attentionNeededPercentage = calculatePercentage(complianceCounts.attentionNeeded, totalPractices);
      complianceCounts.upToDatePercentage = calculatePercentage(complianceCounts.upToDate, totalPractices);
      complianceCounts.inactivePercentage = calculatePercentage(complianceCounts.inactive, totalPractices);

      setComplianceCounts(complianceCounts);
    }
  }, [customerNumber, childPractices])

  const tabPages = [
    <ComplianceReport complianceCounts={complianceCounts} key="0" />,
    <ExpiredItems count={complianceCounts.expiredCount} key="1" />,
    <ExpiringItems count={complianceCounts.expiringCount} key="2" />,
    <BackorderedItems count={complianceCounts.backorderedCount} key="3" />
  ]

  return (
    <div>
      <PageHeader src={deviceIcon} headerName={'Devices: AEDs, Device Kits and Supplies'} />
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-space-between flex-align-center">
        <Tabs value={tabIndex} onChange={(e, value) => setTabIndex(value)}>
          {
            pageTabs.length && pageTabs.map((tab, index) => (
              <Tab key={index} label={tab} />
            ))
          }
        </Tabs>
        <div className="dropdown">
          <Button
            className='m-r-12'
            aria-haspopup="true"
            variant="contained"
            name="export"
            onClick={handleExportClick}
            disabled={exportLoading}
          >
            Export
            <FontAwesomeIcon icon="caret-down" size="sm" className="m-l-8" />
          </Button>
          <Menu
            id="account-menu"
            anchorEl={exportOpenEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right'
            }}
            open={exportOpen}
            onClose={handleExportClose}
            MenuListProps={{
              'aria-labelledby': 'export-button'
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right'
            }}
          >
            <MenuItem onClick={() => handleExportByType('aed')}>
              All Results AEDs
            </MenuItem>
            <MenuItem onClick={() => handleExportByType('dr')}>
              All Results DR Kits
            </MenuItem>
            <MenuItem onClick={() => handleExportByType('kitsByPractice')}>
              AEDs and DR Kits by Practice
            </MenuItem>
            <MenuItem onClick={() => handleExportByType('resultsByPractice')}>
              Report by Practice
            </MenuItem>
            <MenuItem onClick={() => handleExportByType('expired')}>
              Expired Devices
            </MenuItem>
            <MenuItem onClick={() => handleExportByType('expiring')}>
              Expiring Devices
            </MenuItem>
            <MenuItem onClick={() => handleExportByType('backordered')}>
              Backordered Devices
            </MenuItem>
          </Menu>
        </div>
      </Box>
      <Box sx={{ m: 1.5 }}>
        {
          tabPages[tabIndex]
        }
      </Box>
    </div>
  )
}

export default Devices;